import { OrganizationForm } from './OrganizationForm';
import { useCreateOrganization } from '@/hooks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Organization, OrganizationFormData } from '@/lib/schemas';

/**
 * Parse PostgreSQL/API error messages into user-friendly text
 */
function getErrorMessage(error: any): string {
  const message = error?.message || '';

  // Check for PostgreSQL unique constraint violation (code 23505)
  // Error format: "duplicate key value violates unique constraint..."
  // or contains Key (tenant_id, name)=(xxx, NAME) already exists
  if (message.includes('23505') ||
      message.includes('duplicate key') ||
      message.includes('unique constraint') ||
      message.includes('already exists')) {
    // Try to extract the client name from the error
    const nameMatch = message.match(/name\)=\([^,]+,\s*([^)]+)\)/);
    if (nameMatch && nameMatch[1]) {
      return `A client named "${nameMatch[1]}" already exists. Please use a different name.`;
    }
    return 'A client with this name already exists. Please use a different name.';
  }

  // Default message
  return message || 'Failed to create client';
}

interface OrganizationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldSet: 'minimal' | 'full';
  onSave: (organization: Organization) => void;
}

export function OrganizationFormModal({
  open,
  onOpenChange,
  fieldSet,
  onSave
}: OrganizationFormModalProps) {
  const createOrganization = useCreateOrganization();

  const handleSubmit = async (data: OrganizationFormData) => {
    const payload = {
      name: data.name,
      // Phase 5 Batch 6 Phase 3: Minimal fieldSet (inline from project form) always creates client orgs
      is_client: fieldSet === 'minimal' ? true : data.is_client,
      is_consultant: data.is_consultant,
      is_government: data.is_government,
      email: data.email || null,
      phone: data.phone || null,
      // Full fieldSet includes all fields
      ...(fieldSet === 'full' && {
        legal_name: data.legal_name || null,
        address: data.address || null,
        billing_address: data.billing_address || null,
        notes: data.notes || null,
        payment_terms: data.payment_terms ? parseInt(data.payment_terms) : 30,
      }),
      // Minimal fieldSet gets default payment_terms
      ...(fieldSet === 'minimal' && { payment_terms: 30 }),
    };

    try {
      const response = await createOrganization.mutateAsync(payload);
      onSave(response.organization);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Client</DialogTitle>
          <DialogDescription>
            {fieldSet === 'minimal'
              ? 'Add a client quickly. You can edit additional details later.'
              : 'Add a client with complete details.'
            }
          </DialogDescription>
        </DialogHeader>

        <OrganizationForm
          mode="create"
          fieldSet={fieldSet}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />

        {createOrganization.isError && (
          <p className="text-sm text-destructive mt-2">
            {getErrorMessage(createOrganization.error)}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
